import { useEffect } from 'react';

/**
 * Keeps the single <link rel="canonical"> in sync with the current route.
 *
 * The base canonical tag lives in index.html (pointing at "/"). This hook
 * rewrites that one tag while the page is mounted, then restores the previous
 * value on unmount. Rewriting the existing tag (instead of rendering a second
 * one) guarantees exactly one canonical link in <head> at all times, which
 * avoids the duplicate-canonical problem that hurts SEO.
 *
 * The route is resolved against the current origin so the emitted canonical is
 * always an absolute URL (e.g. https://example.com/performance), which is the
 * form search engines recommend.
 */
export default function useCanonical(href: string) {
  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) return undefined;

    const previous = link.getAttribute('href') ?? '/';
    let absolute = href;
    try {
      absolute = new URL(href, window.location.origin).href;
    } catch {
      absolute = href;
    }
    link.setAttribute('href', absolute);

    return () => {
      link.setAttribute('href', previous);
    };
  }, [href]);
}